import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Users, Link as LinkIcon, AlertTriangle, Package, Box, ShieldCheck, 
  Headphones, Building2, ClipboardList, MessageSquare
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";

// Tab Components
import AdminFranchiseesTab from "@/pages/admin/AdminFranchiseesTab";
import AdminOpsManagersTab from "@/pages/admin/AdminOpsManagersTab";
import AdminUsersTab from "@/pages/admin/AdminUsersTab";
import AdminCatalogTab from "@/pages/admin/AdminCatalogTab";
import AdminInventoryTab from "@/pages/admin/AdminInventoryTab";
import AdminRawMaterialsTab from "@/pages/admin/AdminRawMaterialsTab";
import KitchenOutletManagement from "@/pages/admin/KitchenOutletManagement";
import ChecklistManagement from "@/pages/admin/ChecklistManagement";
import ManagementIssuesAdmin from "@/pages/admin/ManagementIssuesAdmin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPage({ user }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("franchisees");
  const [isLoading, setIsLoading] = useState(true);
  
  // Data states
  const [franchisees, setFranchisees] = useState([]);
  const [products, setProducts] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [opsManagers, setOpsManagers] = useState([]);
  const [kitchenInventoryData, setKitchenInventoryData] = useState({ kitchens: [], hq: [] });
  const [rawMaterials, setRawMaterials] = useState([]);
  const [kitchenRawInventory, setKitchenRawInventory] = useState([]);
  const [productRecipes, setProductRecipes] = useState([]);
  
  // Settings state
  const [isUrlDialogOpen, setIsUrlDialogOpen] = useState(false);
  const [salesUrl, setSalesUrl] = useState("");

  // Stock dialog state for inventory tab
  const [isStockDialogOpen, setIsStockDialogOpen] = useState(false);
  const [editingProductForStock, setEditingProductForStock] = useState(null);
  const [stockAdjustment, setStockAdjustment] = useState({ adjustment: 0, reason: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check user permissions
  const hasPermission = (permission) => {
    if (user.is_super_admin) return true;
    return user.permissions?.[permission] ?? false;
  };

  useEffect(() => {
    if (user.role !== "admin") {
      navigate("/dashboard");
      toast.error("Admin access required");
      return;
    }
    
    // Set initial tab based on permissions
    const checkPerm = (perm) => user.is_super_admin || user.permissions?.[perm];
    
    if (!checkPerm("franchisees")) {
      if (checkPerm("products")) setActiveTab("catalog");
      else if (checkPerm("inventory")) setActiveTab("inventory");
      else if (user.is_super_admin) setActiveTab("admins");
    }
    
    // Fetch all data
    loadData();
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const loadData = async () => {
    const checkPerm = (perm) => user.is_super_admin || user.permissions?.[perm];
    
    if (checkPerm("franchisees")) fetchFranchisees();
    if (checkPerm("products") || checkPerm("inventory")) {
      fetchProducts();
      fetchKitchenInventory();
      fetchRawMaterials();
      fetchKitchenRawInventory();
      fetchProductRecipes();
    }
    if (checkPerm("sales_settings")) fetchSalesUrl();
    if (user.is_super_admin) fetchAdminUsers();
    fetchOpsManagers();
  };

  // Data fetch functions
  const fetchFranchisees = async () => {
    try {
      const response = await axios.get(`${API}/admin/franchisees`);
      setFranchisees(response.data);
    } catch (error) {
      console.error("Error fetching franchisees:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      const [productsRes, lowStockRes] = await Promise.all([
        axios.get(`${API}/admin/products`),
        axios.get(`${API}/admin/inventory/low-stock`)
      ]);
      setProducts(productsRes.data);
      setLowStockProducts(lowStockRes.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchKitchenInventory = async () => {
    try {
      const res = await axios.get(`${API}/ops-extended/kitchen-inventory`);
      setKitchenInventoryData(res.data);
    } catch (error) {
      console.error("Error fetching kitchen inventory:", error);
    }
  };

  const fetchRawMaterials = async () => {
    try {
      const res = await axios.get(`${API}/ops-extended/raw-materials`);
      setRawMaterials(res.data);
    } catch (error) {
      console.error("Error fetching raw materials:", error);
    }
  };

  const fetchKitchenRawInventory = async () => {
    try {
      const res = await axios.get(`${API}/ops-extended/kitchen-raw-inventory`);
      setKitchenRawInventory(res.data);
    } catch (error) {
      console.error("Error fetching kitchen raw inventory:", error);
    }
  };

  const fetchProductRecipes = async () => {
    try {
      const res = await axios.get(`${API}/ops-extended/product-recipes`);
      setProductRecipes(res.data);
    } catch (error) {
      console.error("Error fetching product recipes:", error);
    }
  };

  const fetchAdminUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/admins`);
      setAdminUsers(response.data);
    } catch (error) {
      console.error("Error fetching admin users:", error);
    }
  };

  const fetchOpsManagers = async () => {
    try {
      const response = await axios.get(`${API}/admin/ops-managers`);
      setOpsManagers(response.data);
    } catch (error) {
      console.error("Error fetching ops managers:", error);
    }
  };

  const fetchSalesUrl = async () => {
    try {
      const response = await axios.get(`${API}/settings/sales-url`);
      setSalesUrl(response.data.url || "");
    } catch (error) {
      console.error("Error fetching sales URL:", error);
    }
  };

  const handleSalesUrlUpdate = async () => {
    try {
      await axios.put(`${API}/settings/sales-url`, { url: salesUrl });
      toast.success("Sales URL updated");
      setIsUrlDialogOpen(false);
    } catch (error) {
      toast.error("Failed to update sales URL");
    }
  };

  // Stock dialog handlers for inventory tab
  const openStockDialogFromInventory = (product) => {
    setEditingProductForStock(product);
    setStockAdjustment({ adjustment: 0, reason: "" });
    setIsStockDialogOpen(true);
  };

  const handleStockAdjust = async () => {
    if (stockAdjustment.adjustment === 0) {
      toast.error("Enter a non-zero adjustment");
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/admin/products/${editingProductForStock.product_id}/adjust-stock`, {
        adjustment: stockAdjustment.adjustment,
        reason: stockAdjustment.reason || "Manual adjustment"
      });
      toast.success("Stock adjusted");
      setIsStockDialogOpen(false);
      setEditingProductForStock(null);
      fetchProducts();
    } catch (error) {
      toast.error("Failed to adjust stock");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (user.role !== "admin") return null;

  return (
    <div className="flex min-h-screen bg-background overflow-x-hidden max-w-full">
      <Sidebar user={user} activePath="/admin" />
      
      <main className="flex-1 p-4 lg:p-8 ml-0 lg:ml-64 overflow-x-hidden max-w-full min-w-0">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8 gap-4 max-w-full">
          <div className="min-w-0">
            <h1 className="font-heading text-2xl lg:text-4xl text-foreground mb-2">Admin Panel</h1>
            <p className="text-muted-foreground text-sm lg:text-base">Manage franchisees, products, and settings</p>
          </div>
          {hasPermission("sales_settings") && (
            <Button 
              variant="outline"
              className="rounded-full w-full lg:w-auto flex-shrink-0"
              onClick={() => setIsUrlDialogOpen(true)}
              data-testid="configure-sales-url-btn"
            >
              <LinkIcon className="w-4 h-4 mr-2" />
              Configure Sales URL
            </Button>
          )}
        </div>

        {/* Low Stock Alert */}
        {hasPermission("inventory") && lowStockProducts.length > 0 && (
          <Alert className="mb-6 border-yellow-200 bg-yellow-50">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              <strong>{lowStockProducts.length} products</strong> are running low on stock!
              <Button 
                variant="link" 
                className="text-yellow-700 px-2"
                onClick={() => setActiveTab("inventory")}
              >
                View inventory →
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex-wrap h-auto gap-1">
            {hasPermission("franchisees") && (
              <TabsTrigger value="franchisees" data-testid="tab-franchisees" className="text-xs lg:text-sm">
                <Users className="w-4 h-4 mr-1 lg:mr-2" />
                <span className="hidden sm:inline">Franchisees</span>
                <span className="sm:hidden">Users</span>
              </TabsTrigger>
            )}
            {hasPermission("products") && (
              <TabsTrigger value="catalog" data-testid="tab-catalog" className="text-xs lg:text-sm">
                <Package className="w-4 h-4 mr-1 lg:mr-2" />
                <span className="hidden sm:inline">Product Catalog</span>
                <span className="sm:hidden">Products</span>
              </TabsTrigger>
            )}
            {hasPermission("inventory") && (
              <TabsTrigger value="inventory" data-testid="tab-inventory" className="text-xs lg:text-sm">
                <Box className="w-4 h-4 mr-1 lg:mr-2" />
                Inventory
                {lowStockProducts.length > 0 && (
                  <Badge className="ml-1 lg:ml-2 bg-yellow-500 text-xs">{lowStockProducts.length}</Badge>
                )}
              </TabsTrigger>
            )}
            {hasPermission("inventory") && (
              <TabsTrigger value="raw-materials" data-testid="tab-raw-materials" className="text-xs lg:text-sm">
                <Package className="w-4 h-4 mr-1 lg:mr-2" />
                <span className="hidden sm:inline">Raw Materials</span>
                <span className="sm:hidden">Materials</span>
              </TabsTrigger>
            )}
            {user.is_super_admin && (
              <TabsTrigger value="admins" data-testid="tab-admins" className="text-xs lg:text-sm">
                <ShieldCheck className="w-4 h-4 mr-1 lg:mr-2" />
                <span className="hidden sm:inline">Admin Users</span>
                <span className="sm:hidden">Admins</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="ops-managers" data-testid="tab-ops-managers" className="text-xs lg:text-sm">
              <Headphones className="w-4 h-4 mr-1 lg:mr-2" />
              <span className="hidden sm:inline">Ops Managers</span>
              <span className="sm:hidden">Ops</span>
            </TabsTrigger>
            <TabsTrigger value="kitchens" data-testid="tab-kitchens" className="text-xs lg:text-sm">
              <Building2 className="w-4 h-4 mr-1 lg:mr-2" />
              <span className="hidden sm:inline">Kitchens & Outlets</span>
              <span className="sm:hidden">Kitchens</span>
            </TabsTrigger>
            <TabsTrigger value="checklists" data-testid="tab-checklists" className="text-xs lg:text-sm">
              <ClipboardList className="w-4 h-4 mr-1 lg:mr-2" />
              <span className="hidden sm:inline">Checklists</span>
              <span className="sm:hidden">Lists</span>
            </TabsTrigger>
            <TabsTrigger value="management-issues" data-testid="tab-issues" className="text-xs lg:text-sm">
              <MessageSquare className="w-4 h-4 mr-1 lg:mr-2" />
              <span className="hidden sm:inline">Issues</span>
              <span className="sm:hidden">Issues</span>
            </TabsTrigger>
          </TabsList>

          {/* Franchisees Tab */}
          {hasPermission("franchisees") && (
            <TabsContent value="franchisees">
              <AdminFranchiseesTab 
                franchisees={franchisees} 
                isLoading={isLoading} 
                onRefresh={fetchFranchisees} 
              />
            </TabsContent>
          )}

          {/* Catalog Tab */}
          {hasPermission("products") && (
            <TabsContent value="catalog">
              <AdminCatalogTab 
                products={products}
                productRecipes={productRecipes}
                rawMaterials={rawMaterials}
                isLoading={isLoading}
                onRefresh={fetchProducts}
                onRefreshRecipes={fetchProductRecipes}
              />
            </TabsContent>
          )}

          {/* Inventory Tab */}
          {hasPermission("inventory") && (
            <TabsContent value="inventory">
              <AdminInventoryTab 
                products={products}
                lowStockProducts={lowStockProducts}
                kitchenInventoryData={kitchenInventoryData}
                onOpenStockDialog={openStockDialogFromInventory}
              />
            </TabsContent>
          )}

          {/* Raw Materials Tab */}
          {hasPermission("inventory") && (
            <TabsContent value="raw-materials">
              <AdminRawMaterialsTab 
                rawMaterials={rawMaterials}
                kitchenRawInventory={kitchenRawInventory}
                isLoading={isLoading}
                onRefresh={fetchRawMaterials}
                onRefreshKitchenInventory={fetchKitchenRawInventory}
              />
            </TabsContent>
          )}

          {/* Admin Users Tab */}
          {user.is_super_admin && (
            <TabsContent value="admins">
              <AdminUsersTab 
                adminUsers={adminUsers}
                isLoading={isLoading}
                onRefresh={fetchAdminUsers}
                currentUser={user}
              />
            </TabsContent>
          )}

          {/* Ops Managers Tab */}
          <TabsContent value="ops-managers">
            <AdminOpsManagersTab 
              opsManagers={opsManagers}
              isLoading={isLoading}
              onRefresh={fetchOpsManagers}
            />
          </TabsContent>

          {/* Kitchens & Outlets Tab */}
          <TabsContent value="kitchens">
            <KitchenOutletManagement />
          </TabsContent>

          {/* Checklists Tab */}
          <TabsContent value="checklists">
            <ChecklistManagement />
          </TabsContent>

          {/* Management Issues Tab */}
          <TabsContent value="management-issues">
            <ManagementIssuesAdmin />
          </TabsContent>
        </Tabs>

        {/* Sales URL Dialog */}
        <Dialog open={isUrlDialogOpen} onOpenChange={setIsUrlDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Configure Sales Dashboard URL</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <Alert>
                <LinkIcon className="h-4 w-4" />
                <AlertDescription>
                  This URL will be displayed in an iframe on the Sales page for all franchisees.
                </AlertDescription>
              </Alert>
              <div className="space-y-2">
                <Label>POS Dashboard URL</Label>
                <Input
                  type="url"
                  value={salesUrl}
                  onChange={(e) => setSalesUrl(e.target.value)}
                  placeholder="https://your-pos-dashboard.com"
                />
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setIsUrlDialogOpen(false)} className="rounded-full">Cancel</Button>
              <Button onClick={handleSalesUrlUpdate} className="rounded-full">Save URL</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Stock Adjustment Dialog (for inventory tab) */}
        <Dialog open={isStockDialogOpen} onOpenChange={setIsStockDialogOpen}>
          <DialogContent data-testid="stock-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Adjust Stock</DialogTitle>
            </DialogHeader>
            {editingProductForStock && (
              <div className="py-4 space-y-4">
                <div className="flex items-center gap-4 p-4 bg-muted rounded-xl">
                  {editingProductForStock.image_url && (
                    <img src={editingProductForStock.image_url} alt={editingProductForStock.name} className="w-16 h-16 rounded-lg object-cover" />
                  )}
                  <div>
                    <p className="font-semibold">{editingProductForStock.name}</p>
                    <p className="text-sm text-muted-foreground">Current stock: <strong>{editingProductForStock.stock_quantity || 0}</strong></p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Adjustment (+/-)</Label>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="rounded-full"
                      onClick={() => setStockAdjustment({ ...stockAdjustment, adjustment: stockAdjustment.adjustment - 10 })}
                    >
                      -
                    </Button>
                    <Input
                      type="number"
                      value={stockAdjustment.adjustment}
                      onChange={(e) => setStockAdjustment({ ...stockAdjustment, adjustment: parseInt(e.target.value) || 0 })}
                      className="text-center text-xl font-bold w-24"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      className="rounded-full"
                      onClick={() => setStockAdjustment({ ...stockAdjustment, adjustment: stockAdjustment.adjustment + 10 })}
                    >
                      +
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground text-center">
                    New stock: <strong className={stockAdjustment.adjustment >= 0 ? "text-green-600" : "text-red-600"}>
                      {Math.max(0, (editingProductForStock.stock_quantity || 0) + stockAdjustment.adjustment)}
                    </strong>
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Reason (optional)</Label>
                  <Input
                    value={stockAdjustment.reason}
                    onChange={(e) => setStockAdjustment({ ...stockAdjustment, reason: e.target.value })}
                    placeholder="e.g., New shipment received"
                  />
                </div>
              </div>
            )}
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setIsStockDialogOpen(false)} className="rounded-full">Cancel</Button>
              <Button onClick={handleStockAdjust} disabled={isSubmitting} className="rounded-full" data-testid="save-stock-btn">
                Adjust Stock
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
